import { noop } from 'lodash';
import React, { forwardRef, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import Checkbox from '@material-ui/core/Checkbox';
import Typography from '@material-ui/core/Typography';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import {
  ActionButtonHandle,
  ScrollableSource,
  Sort,
  TableField,
  TableFieldColumn,
  TableFieldContext,
  TableFieldHandle,
  TableFieldSelectedContext,
} from '@eas/common-web';
import { TableFieldHeaderProps } from '@eas/common-web/dist/types/components/table-field/table-field-types';
import { DictionaryObject, EsFileRegulation } from '@models';
import { RegulationType } from '@enums';
import { RegulationDetailDialog } from './regulation-dialog-detail';

export interface RegulationSection {
  type: RegulationType;
  title: React.ReactNode;
  source?: ScrollableSource<EsFileRegulation>;
}

export interface RegulationSectionTableProps {
  section: RegulationSection;
  columns: TableFieldColumn<EsFileRegulation>[];
  disabled: boolean;
  tableRef: React.RefObject<TableFieldHandle>;
  editRef: React.RefObject<ActionButtonHandle>;
  regulationEntityTypes: DictionaryObject[];
  regulationTypes: DictionaryObject[];
  getMasterIndexById: (id?: string) => number;
  getMasterIndexesByRows: (rows: EsFileRegulation[]) => number[];
  updateMasterSelection: (masterIndexes: number[], selected: boolean) => void;
  setSelectedIndexes: (indexes: number[]) => void;
  clearTableSelections: () => void;
  handleSortChange: (
    typeSource: ScrollableSource<EsFileRegulation>,
    sort: Sort[]
  ) => void;
}

export function RegulationSectionTable({
  section,
  columns,
  disabled,
  tableRef,
  editRef,
  regulationEntityTypes,
  regulationTypes,
  getMasterIndexById,
  getMasterIndexesByRows,
  updateMasterSelection,
  setSelectedIndexes,
  clearTableSelections,
  handleSortChange,
}: RegulationSectionTableProps) {
  const typeSource = section.source;

  if (!typeSource || (typeSource.count ?? 0) === 0) {
    return null;
  }

  const HeaderComponent = forwardRef<HTMLDivElement, TableFieldHeaderProps>(
    function RegulationTableHeader(props, ref) {
      const {
        actionColumnWidth,
        disabled: tableDisabled,
        filteredColumns,
        selectRows,
        sorts,
        value,
      } = useContext(TableFieldContext);
      const { selectedIndexes: tableSelectedIndexes } = useContext(
        TableFieldSelectedContext
      );

      return (
        <div
          ref={ref}
          style={{
            boxSizing: 'border-box',
            display: 'flex',
            overflow: 'visible',
            minWidth: '100%',
            width: 'fit-content',
            height: 35,
            padding: '0 16px',

            color: 'black',
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }}
        >
          <div
            style={{
              alignItems: 'center',
              display: 'inline-flex',
              flexShrink: 0,
              height: 35,
              width: actionColumnWidth,
            }}
          >
            {!tableDisabled && value.length > 0 && (
              <Checkbox
                checked={tableSelectedIndexes.length > 0}
                color="primary"
                style={{ padding: 0 }}
                onClick={(event) => {
                  event.stopPropagation();

                  const localIndexes = typeSource.items.map(
                    (_, index) => index
                  );
                  const masterIndexes = getMasterIndexesByRows(
                    typeSource.items
                  );

                  if (tableSelectedIndexes.length > 0) {
                    selectRows([], false);
                    updateMasterSelection(masterIndexes, false);
                  } else {
                    if (masterIndexes.length !== typeSource.items.length) {
                      setSelectedIndexes([]);
                      clearTableSelections();
                      return;
                    }

                    selectRows(localIndexes, false);
                    updateMasterSelection(masterIndexes, true);
                  }
                }}
              />
            )}
          </div>
          {filteredColumns.map((column, index) => {
            const sort = sorts.find((item) => item.datakey === column.datakey);

            return (
              <div
                key={`${column.datakey}-${index}`}
                style={{
                  cursor: column.sortkey ? 'pointer' : undefined,
                  display: 'flex',
                  flexShrink: 0,
                  lineHeight: '35px',
                  width: column.width,
                }}
                onClick={() => {
                  if (column.sortkey) {
                    props.onColumnClick(column.datakey);
                  }
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    flex: '1 1 auto',
                    margin: 'auto',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {column.name}
                </span>
                {sort && (
                  <ArrowDropDownIcon
                    style={{
                      alignSelf: 'center',
                      flexShrink: 0,
                      transform:
                        sort.order === 'ASC' ? 'scaleY(-1)' : undefined,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      );
    }
  );

  return (
    <div style={{ width: '100%' }}>
      <TableField<EsFileRegulation>
        ref={tableRef}
        disabled={disabled}
        columns={columns}
        HeaderComponent={HeaderComponent}
        showToolbar={false}
        value={typeSource.items}
        onChange={noop}
        onSelect={(row, index) => {
          const masterIndex = getMasterIndexById(
            row?.id ?? typeSource.items[index]?.id
          );

          if (masterIndex !== -1) {
            updateMasterSelection([masterIndex], row !== null);
          } else {
            setSelectedIndexes([]);
            clearTableSelections();
          }
        }}
        onSortChange={(sort) => handleSortChange(typeSource, sort)}
        defaultSort={typeSource.getParams()?.sort ?? []}
        onRowClick={(_, index) => {
          if (!disabled) {
            const masterIndex = getMasterIndexById(typeSource.items[index]?.id);

            if (masterIndex !== -1) {
              clearTableSelections();
              tableRef.current?.setSelectedIndex(index);
              tableRef.current?.setSelectedIndexes([index]);
              setSelectedIndexes([masterIndex]);
              window.setTimeout(() => editRef.current?.executeAction());
            } else {
              setSelectedIndexes([]);
              clearTableSelections();
            }
          }
        }}
        maxRows={Math.max(typeSource.items.length, 1)}
        showDetailBtnCond={() => true}
        showRadioCond={() => false}
        showCheckboxCond={() => true}
        FormFieldsComponent={() => (
          <div style={{ width: 700 }}>
            <Typography variant="body1" style={{ marginBottom: 8 }}>
              <FormattedMessage
                id="ES__REGULATIONS__DESCRIPTION"
                defaultMessage="Předpis:"
              />
            </Typography>
            <RegulationDetailDialog
              regulationEntityTypes={regulationEntityTypes}
              regulationTypes={regulationTypes}
              disabled={disabled}
            />
          </div>
        )}
      />
    </div>
  );
}
